import React, { useState, useMemo } from "react";
import { useAdminStore } from "../stores/adminStore";
import {
  DeleteButton,
  Dropdown,
  PermissionWrapper,
  useConfirmationStore,
} from "components";
import { crmManagerLabel, customCrmManagerOption } from "./crmManagerOptions";

// Компонент таблиці користувачів з можливістю редагування ролей
const UsersTable = React.memo(({ users, onRemove }) => {
  // Отримуємо необхідні дані та дії зі стору
  const { roles, updateUserRole, updateUserCrmManager, updateUserTeamLead } = useAdminStore.getState();
  const crmManagers = useAdminStore((state) => state.crmManagers);
  // Достаємо методи зі стору підтвердження
  const { showConfirmation } = useConfirmationStore.getState();

  // Локальний стан для керування ролей в дропдауні
  const [editingUserId, setEditingUserId] = useState(null);
  // Те саме для колонки «Менеджер CRM»
  const [editingCrmUserId, setEditingCrmUserId] = useState(null);
  // І для колонки «Тімлід»
  const [editingLeadUserId, setEditingLeadUserId] = useState(null);

  // Тімліди для Dropdown — будь-який інший користувач; 0 («—») — без команди.
  // За цим VMS показує тімліду штрафи й порушення підопічних
  const teamLeadOptions = useMemo(
    () => [
      { value: 0, label: "—" },
      ...users
        .slice()
        .sort((a, b) => a.username.localeCompare(b.username, "uk"))
        .map((u) => ({ value: u.id, label: `${u.username} · ${u.id}` })),
    ],
    [users],
  );
  const teamLeadName = (id) => {
    const lead = users.find((u) => u.id === id);
    return lead ? lead.username : id ? `#${id}` : "";
  };

  // Обробник зміни тімліда: сам собі тімлідом бути не може
  const handleTeamLeadChange = (userId, value) => {
    const next = value && value !== userId ? value : null;
    const currentUser = users.find((user) => user.id === userId);
    if (currentUser && (currentUser.team_lead_id ?? null) !== next) {
      updateUserTeamLead(userId, next);
    }
    setEditingLeadUserId(null);
  };

  // Менеджери CRM для Dropdown: «id · ПІБ», щоб обирати за ім'ям; 0 («—») — без прив'язки.
  // У клітинці показуємо лише id — він компактний і корисніший за ім'я
  const crmManagerOptions = useMemo(
    () => [
      { value: 0, label: "—" },
      ...crmManagers.map((m) => ({ value: m.id, label: crmManagerLabel(m) })),
    ],
    [crmManagers],
  );
  const crmManagerTitle = (id) => {
    if (!id) return "";
    const m = crmManagers.find((x) => x.id === id);
    return m ? m.full_name : "Ще немає в списку CRM — ПІБ з'явиться після синхронізації";
  };

  // Обробник зміни менеджера CRM
  const handleCrmManagerChange = (userId, value) => {
    const next = value ? value : null;
    const currentUser = users.find((user) => user.id === userId);
    if (currentUser && (currentUser.crm_manager_id ?? null) !== next) {
      updateUserCrmManager(userId, next);
    }
    setEditingCrmUserId(null);
  };

  // Трансформація ролей для компонента Dropdown
  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        value: role.id,
        label: role.name,
      })),
    [roles],
  );

  // Обробник зміни ролі
  const handleRoleChange = (userId, newRoleId) => {
    const currentUser = users.find((user) => user.id === userId);
    // Якщо роль не змінилась - вихід з режиму редагування
    if (currentUser && currentUser.role_id === newRoleId) {
      setEditingUserId(null);
      return;
    }

    // Викликаємо екшн зі стору для оновлення ролі
    updateUserRole(userId, newRoleId);
    // Виходимо з режиму редагування
    setEditingUserId(null);
  };

  // Обробник видалення користувача з підтвердженням
  const handleRemoveUser = (user) => {
    showConfirmation({
      title: "Підтвердити видалення",
      message: `Ви впевнені, що хочете видалити користувача "${user.username}"? Цю дію не можна скасувати.`,
      onConfirm: () => onRemove(user.id),
    });
  };

  return (
    <div id="users-table">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Ім'я</th>
            <th>Пошта</th>
            <th>Оновлено</th>
            <th>Роль</th>
            <th>Менеджер CRM</th>
            <th>Тімлід</th>
            <PermissionWrapper requiredPermission="users:manage">
              <th>Дії</th>
            </PermissionWrapper>
          </tr>
        </thead>
        <tbody>
          {/* Перевіряємо, чи є користувачі */}
          {users.length === 0 ? (
            <tr>
              <td colSpan={8} className="empty-row">
                Немає користувачів
              </td>
            </tr>
          ) : (
            users.map((user) => {
              // Знаходимо об'єкт ролі за ID, щоб отримати її назву
              const role = roles.find((r) => r.id === user.role_id);
              const roleName = role ? role.name : "Не призначено";

              return (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    {new Intl.DateTimeFormat("uk-UA", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    }).format(new Date(user.updated_at))}
                  </td>
                  <PermissionWrapper
                    requiredPermission="users:manage"
                    fallback={<td>{roleName}</td>}
                  >
                    <td onDoubleClick={() => setEditingUserId(user.id)}>
                      {editingUserId === user.id ? (
                        // РЕЖИМ РЕДАГУВАННЯ: показуємо Dropdown
                        <Dropdown
                          options={roleOptions}
                          selected={user.role_id}
                          onChange={(newRoleId) => handleRoleChange(user.id, newRoleId)}
                          mode="select"
                        />
                      ) : (
                        // РЕЖИМ ПЕРЕГЛЯДУ: показуємо назву ролі
                        <span className="clickable">{roleName}</span>
                      )}
                    </td>
                  </PermissionWrapper>
                  <PermissionWrapper
                    requiredPermission="users:manage"
                    fallback={<td title={crmManagerTitle(user.crm_manager_id)}>{user.crm_manager_id || "—"}</td>}
                  >
                    <td onDoubleClick={() => setEditingCrmUserId(user.id)}>
                      {editingCrmUserId === user.id ? (
                        <Dropdown
                          options={crmManagerOptions}
                          selected={user.crm_manager_id ?? 0}
                          onChange={(value) => handleCrmManagerChange(user.id, value)}
                          mode="autocomplete"
                          placeholder="ПІБ або id"
                          customOption={customCrmManagerOption}
                          fixed
                          listMinWidth={360}
                        />
                      ) : (
                        <span
                          className="clickable"
                          title={crmManagerTitle(user.crm_manager_id) || "Подвійний клік — змінити"}
                        >
                          {user.crm_manager_id || "—"}
                        </span>
                      )}
                    </td>
                  </PermissionWrapper>
                  <PermissionWrapper
                    requiredPermission="users:manage"
                    fallback={<td>{teamLeadName(user.team_lead_id) || "—"}</td>}
                  >
                    <td onDoubleClick={() => setEditingLeadUserId(user.id)}>
                      {editingLeadUserId === user.id ? (
                        <Dropdown
                          options={teamLeadOptions.filter((o) => o.value !== user.id)}
                          selected={user.team_lead_id ?? 0}
                          onChange={(value) => handleTeamLeadChange(user.id, value)}
                          mode="autocomplete"
                          placeholder="Ім'я або id"
                          fixed
                          listMinWidth={280}
                        />
                      ) : (
                        <span className="clickable" title="Подвійний клік — змінити">
                          {teamLeadName(user.team_lead_id) || "—"}
                        </span>
                      )}
                    </td>
                  </PermissionWrapper>
                  <PermissionWrapper requiredPermission="users:manage">
                    <td>
                      <DeleteButton onDelete={() => handleRemoveUser(user)} />
                    </td>
                  </PermissionWrapper>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
});

export default UsersTable;
